import * as THREE from "three";

function makeAtlas() {
  const S = 128, G = 2, W = S * G;
  const data = new Uint8Array(W * W * 4);
  const hash = (x, y, s) => { const h = Math.sin(x * 127.1 + y * 311.7 + s * 74.7) * 43758.5453; return h - Math.floor(h); };
  const noise = (x, y, s) => {
    const xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
    const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
  };
  const fbm = (x, y, s) => { let t = 0, a = 0.5; for (let o = 0; o < 5; o++) { t += a * noise(x, y, s); x *= 2.03; y *= 2.03; a *= 0.5; } return t; };
  for (let v = 0; v < G * G; v++) {
    const ox = (v % G) * S, oy = Math.floor(v / G) * S;
    for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
      const u = (x / S) * 2 - 1, w = (y / S) * 2 - 1;
      const r = Math.hypot(u, (w + 0.12) * 1.5);
      const n = fbm(x / 22 + v * 7.3, y / 22, v);
      const base = Math.max(0, 1 - r);
      const dens = Math.min(1, Math.max(0, (base * 1.25 + n - 0.95) * 2.2)) * Math.min(1, base * 3);
      const lit = Math.min(1, Math.max(0, 0.25 + 0.6 * (w * 0.5 + 0.5) + (n - 0.5) * 0.8));
      const o = ((oy + y) * W + ox + x) * 4;
      data[o] = dens * 255; data[o + 1] = lit * 255; data[o + 2] = 0; data[o + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, W, W, THREE.RGBAFormat);
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

const vert = /* glsl */ `
attribute vec3 offset;
attribute vec4 params;
uniform vec3 uTravel;
uniform vec3 uBoxMin;
uniform vec3 uBoxSize;
uniform vec2 uSunScreen;
uniform float uSize;
varying vec2 vUv;
varying float vAlpha;
varying float vFog;
varying float vSun;
void main() {
  vec3 p = offset + uTravel;
  p.xz = uBoxMin.xz + mod(p.xz - uBoxMin.xz, uBoxSize.xz);
  vec2 q = (p.xz - uBoxMin.xz) / uBoxSize.xz;
  float edge = smoothstep(0.0, 0.14, q.x) * smoothstep(1.0, 0.86, q.x) * smoothstep(0.0, 0.14, q.y) * smoothstep(1.0, 0.86, q.y);
  vec4 mv = viewMatrix * vec4(p, 1.0);
  float dist = -mv.z;
  mv.xy += position.xy * vec2(params.x * params.z, params.x * 0.5) * uSize;
  gl_Position = projectionMatrix * mv;
  float cell = params.y;
  vUv = (position.xy * 0.5 + 0.5) * 0.5 + vec2(mod(cell, 2.0), floor(cell / 2.0)) * 0.5;
  vFog = smoothstep(18.0, 75.0, dist);
  vAlpha = edge * smoothstep(0.6, 3.5, dist) * smoothstep(90.0, 60.0, dist) * params.w;
  vec2 sc = gl_Position.xy / gl_Position.w;
  vSun = clamp(1.0 - length((sc - uSunScreen) * vec2(0.6, 1.0)) * 0.6, 0.0, 1.0);
}`;

const frag = /* glsl */ `
precision highp float;
uniform sampler2D uAtlas;
uniform float uFade;
uniform float uWarm;
varying vec2 vUv;
varying float vAlpha;
varying float vFog;
varying float vSun;
void main() {
  vec4 t = texture2D(uAtlas, vUv);
  float a = t.r * vAlpha * uFade;
  if (a < 0.003) discard;
  float sunK = 0.45 + 0.55 * vSun;
  vec3 coolShade = vec3(0.40, 0.46, 0.56);
  vec3 coolLit = vec3(0.97, 0.985, 1.0);
  vec3 warmShade = vec3(0.30, 0.25, 0.28);
  vec3 warmLit = vec3(1.0, 0.80, 0.60);
  vec3 shade = mix(coolShade, warmShade, uWarm);
  vec3 lit = mix(coolLit, warmLit, uWarm);
  vec3 col = mix(shade, lit, t.g * sunK);
  vec3 fogCool = vec3(0.72, 0.78, 0.86);
  vec3 fogWarm = vec3(0.93, 0.66, 0.46);
  col = mix(col, mix(fogCool, fogWarm, uWarm), vFog * 0.65);
  gl_FragColor = vec4(col * a, a);
}`;

export function createClouds(count) {
  const boxMin = new THREE.Vector3(-46, -3.4, -80);
  const boxSize = new THREE.Vector3(92, 2.6, 86);
  const geo = new THREE.InstancedBufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
  geo.setIndex([0, 1, 2, 0, 2, 3]);
  const off = new Float32Array(count * 3);
  const par = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) {
    off[i * 3] = boxMin.x + Math.random() * boxSize.x;
    off[i * 3 + 1] = boxMin.y + Math.pow(Math.random(), 1.6) * boxSize.y;
    off[i * 3 + 2] = boxMin.z + Math.random() * boxSize.z;
    par[i * 4] = 2.5 + Math.random() * 6.5;
    par[i * 4 + 1] = Math.floor(Math.random() * 4);
    par[i * 4 + 2] = Math.random() > 0.5 ? 1 : -1;
    par[i * 4 + 3] = 0.35 + Math.random() * 0.45;
  }
  geo.setAttribute("offset", new THREE.InstancedBufferAttribute(off, 3));
  geo.setAttribute("params", new THREE.InstancedBufferAttribute(par, 4));
  geo.instanceCount = count;
  const material = new THREE.ShaderMaterial({
    vertexShader: vert,
    fragmentShader: frag,
    uniforms: {
      uAtlas: { value: makeAtlas() },
      uTravel: { value: new THREE.Vector3() },
      uBoxMin: { value: boxMin },
      uBoxSize: { value: boxSize },
      uSunScreen: { value: new THREE.Vector2(0.7, 0.3) },
      uFade: { value: 0 },
      uWarm: { value: 1 },
      uSize: { value: 1 },
    },
    transparent: true,
    depthTest: false,
    depthWrite: false,
    blending: THREE.CustomBlending,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
  });
  const mesh = new THREE.Mesh(geo, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 5;
  return { mesh, material };
}
